import React, { Fragment, useEffect, useMemo, useState } from "react";
import { getUserDetails } from "../../../config/apisauce";
import { usePage } from "@inertiajs/react";
import { BasicProfileCard } from "../BasicProfileCard";
import { Box, Card, Grid2 as Grid, Stack, Typography } from "@mui/material";
import { filterArray } from "../../../helpers/arrayHelper";
import { COLLAB_INTEREST, COLLAB_INTEREST_NSFW, COLLAB_INTEREST_SFW, CONTENT_INTEREST, CONTENT_INTEREST_NSFW, CONTENT_INTEREST_SFW, PLATFORMS_KEY, SOCIALS_KEY } from "../../../enums/generalEnums";
import useStore from "../../../states/store";
import { EditHeader } from "./EditHeader";
import { ProfileInterests } from "../../admin/adminHomePage/profileDetails/ProfileInterests";
import { LinksCard } from "../../admin/adminHomePage/profileDetails/LinksCard";
import { GenericDialog } from "../../../components/generic/GenericDialog";
import { EDIT_PROFILE_FORMS } from "../../../enums/editProfileEnums";
import { EditPlatformsForm } from "./EditPlatformsForm";
import { EditSocialsForm } from "./EditSocialsForm";
import { BIO_KEY } from "../../../enums/profileSetupEnums";
import { EditAboutMe } from "./EditAboutMe";
import { EditCollabInterestForm } from "./EditCollabInterestForm";
import { EditContentInterestForm } from "./EditContentInterestForm";

const EditProfilePage = () => {
  const [, { openLoader, closeLoader }] = useStore();
  const { auth } = usePage().props;
  const [creatorDetails, setCreatorDetails] = useState({});
  const [selectedEditFormDetails, setSelectedEditFormDetails] = useState({});
  const [openEditDialog, setOpenEditDialog] = useState(false);

  const fetchCreatorDetails = async () => {
    openLoader();
    const { ok, data } = await getUserDetails({ user_id: auth?.user?.id });
    if (ok) {
      setCreatorDetails(data);
    }
    closeLoader();
  };

  const handleOpenEditDialog = (index) => {
    setSelectedEditFormDetails(EDIT_PROFILE_FORMS[index]);
    setOpenEditDialog(true);
  };

  useEffect(() => {
    fetchCreatorDetails();
  }, []);

  const socialsAndPlatforms = useMemo(() => {
    return {
      platforms: filterArray(creatorDetails?.user?.category_values, PLATFORMS_KEY, 'category.type') ?? [],
      socials: filterArray(creatorDetails?.user?.category_values, SOCIALS_KEY, 'category.type') ?? []
    };
  }, [creatorDetails]);

  const genericFormProps = {
    creatorDetails: creatorDetails,
    handleClose: () => setOpenEditDialog(false),
    refetchDetails: () => fetchCreatorDetails(),
  };

  const forms = {
    [PLATFORMS_KEY]: <EditPlatformsForm
      platformValues={socialsAndPlatforms?.platforms}
      {...genericFormProps}
    />,
    [SOCIALS_KEY]: <EditSocialsForm
      enableFollowerCount={false}
      socialsValues={socialsAndPlatforms?.socials}
      {...genericFormProps}
    />,
    [BIO_KEY]: <EditAboutMe {...genericFormProps} />,
    [COLLAB_INTEREST]: <EditCollabInterestForm {...genericFormProps} />,
    [CONTENT_INTEREST]: <EditContentInterestForm {...genericFormProps} />
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} color="primary.main" mb={1.5}>Edit Profile</Typography>
      <BasicProfileCard userDetails={creatorDetails} refetchData={fetchCreatorDetails} />
      <Grid container mt={2} spacing={2}>
        <Grid item size={{ xs: 12, sm: 5, lg: 4 }}>
          <Card>
            <EditHeader label={'Platforms'} action={() => handleOpenEditDialog(0)} />
            <Box sx={{ minHeight: 200 }}>
              <Stack gap={2} mt={'15px'}>
                {
                  socialsAndPlatforms?.platforms?.map((platform, index) => {
                    return (
                      <LinksCard
                        icon={platform?.category?.name}
                        key={`platform-key-${index}`}
                        value={platform?.value}
                      />
                    );
                  })
                }
              </Stack>
            </Box>
            <EditHeader label={'Socials'} sx={{ margin: '10px' }} action={() => handleOpenEditDialog(1)} />
            <Box sx={{ minHeight: 200 }}>
              <Stack gap={2}>
                {
                  socialsAndPlatforms?.socials?.map((social, index) => {
                    return (
                      <LinksCard
                        icon={social?.category?.name}
                        key={`social-key-${index}`}
                        value={social?.value}
                      />
                    );
                  })
                }
              </Stack>
            </Box>
          </Card>
        </Grid>
        <Grid item size={{ xs: 12, sm: 7, lg: 8 }}>
          <Stack gap={2}>
            <Card sx={{ minHeight: '404px' }}>
              <EditHeader label={'About Me'} action={() => handleOpenEditDialog(2)} />
              <Typography mt={1.5}>{creatorDetails?.bio}</Typography>
            </Card>
            <ProfileInterests
              title={<EditHeader label={'Collab Interest'} mb={1.5} action={() => handleOpenEditDialog(3)} />}
              interests={creatorDetails?.user?.collab_interest}
              sfwKey={COLLAB_INTEREST_SFW}
              nsfwKey={COLLAB_INTEREST_NSFW}
            />
            <ProfileInterests
              title={<EditHeader label={'Content Interest'} mb={1.5} action={() => handleOpenEditDialog(4)} />}
              interests={creatorDetails?.user?.content_interest}
              sfwKey={CONTENT_INTEREST_SFW}
              nsfwKey={CONTENT_INTEREST_NSFW}
            />
          </Stack>
        </Grid>
      </Grid>
      <GenericDialog
        title={`Edit ${selectedEditFormDetails?.label}`}
        width={'854px'}
        openDialog={openEditDialog}
        handleClose={() => setOpenEditDialog(false)}
      >
        {forms?.[selectedEditFormDetails?.key]}
      </GenericDialog>
    </Box>
  );
};

export default EditProfilePage;